import type {
  ScorecardCheckDto,
  SnapshotDto,
} from '@internal/scorecards-common';
import { useEffect, useReducer } from 'react';

import type { Entity } from '@backstage/catalog-model';
import { scorecardsApiRef } from '../api/ScorecardsApi';
import { useApi } from '@backstage/core-plugin-api';
import { useEntity } from '@backstage/plugin-catalog-react';
import { formatFactKey } from '../utils/formatFactKey';

export interface FindingRow {
  id: string;
  finding: string;
  reason: string;
}

export interface CheckRow {
  id: string;
  check: ScorecardCheckDto;
  snapshot: SnapshotDto | null;
  findingsCount: number;
  findings: FindingRow[];
}

function extractFindingFields(
  item: unknown,
  fallback: string,
): { finding: string; detail: string | undefined } {
  if (typeof item === 'string' || typeof item === 'number') {
    return { finding: String(item), detail: undefined };
  }
  if (item && typeof item === 'object') {
    const obj = item as Record<string, unknown>;
    let titleKey: string | undefined;
    for (const field of ['name', 'id', 'title']) {
      if (typeof obj[field] === 'string') {
        titleKey = field;
        break;
      }
    }
    if (!titleKey) {
      titleKey = Object.keys(obj).find(k => typeof obj[k] === 'string');
    }
    if (titleKey) {
      const finding = obj[titleKey] as string;
      const detail = Object.entries(obj)
        .filter(
          ([k, v]) =>
            k !== titleKey && (typeof v === 'string' || typeof v === 'number'),
        )
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(' · ');
      return { finding, detail: detail || undefined };
    }
  }
  return { finding: fallback, detail: undefined };
}

interface FactEntry {
  description?: string;
  value: unknown;
}

function stripInternalKeys(factValue: unknown): unknown {
  if (!factValue || typeof factValue !== 'object') return factValue;
  return Object.fromEntries(
    Object.entries(factValue as Record<string, unknown>).filter(
      ([k]) => !k.startsWith('_'),
    ),
  );
}

function findingsFromFactValue(factValue: unknown): FindingRow[] {
  if (!factValue || typeof factValue !== 'object') return [];

  const fv = factValue as Record<string, unknown>;
  const failingIndices = fv._failing_indices as
    | Record<string, number[]>
    | undefined;
  const failingScalars = fv._failing_scalars as string[] | undefined;

  const findings: FindingRow[] = [];
  for (const [key, raw] of Object.entries(fv)) {
    if (key === '_failing_indices' || key === '_failing_scalars') continue;
    if (!raw || typeof raw !== 'object' || !('value' in (raw as object)))
      continue;
    const entry = raw as FactEntry;
    const reason = entry.description ?? key;
    const prettyKey = formatFactKey(key);

    if (Array.isArray(entry.value)) {
      entry.value.forEach((item, idx) => {
        if (failingIndices && !(failingIndices[key] ?? []).includes(idx))
          return;
        const { finding, detail } = extractFindingFields(item, prettyKey);
        findings.push({
          id: `${key}-${idx}`,
          finding,
          reason: detail ?? reason,
        });
      });
    } else {
      if (failingScalars && !failingScalars.includes(key)) continue;
      const val = entry.value;
      const display =
        typeof val === 'object' && val !== null
          ? JSON.stringify(val)
          : String(val);
      findings.push({ id: key, finding: display, reason });
    }
  }

  return findings;
}

function resolveProjectPath(entity: Entity): string {
  return (
    entity.metadata.annotations?.['gitlab.com/project-slug'] ?? ''
  ).toLowerCase();
}

type State = { rows: CheckRow[]; isLoading: boolean; error: Error | undefined };
type Action =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_SUCCESS'; rows: CheckRow[] }
  | { type: 'FETCH_ERROR'; error: Error };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'FETCH_START':
      return { rows: [], isLoading: true, error: undefined };
    case 'FETCH_SUCCESS':
      return { rows: action.rows, isLoading: false, error: undefined };
    case 'FETCH_ERROR':
      return { rows: [], isLoading: false, error: action.error };
    default:
      return state;
  }
}

export function useProjectScorecard(): {
  rows: CheckRow[];
  isLoading: boolean;
  error: Error | undefined;
} {
  const api = useApi(scorecardsApiRef);
  const { entity } = useEntity();
  const projectPath = resolveProjectPath(entity);

  const [state, dispatch] = useReducer(reducer, {
    rows: [],
    isLoading: true,
    error: undefined,
  });

  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'FETCH_START' });

    Promise.all([api.getChecks(), api.getEntitySnapshots(projectPath)])
      .then(([checks, snapshots]) => {
        if (cancelled) return;
        const snapshotByCheckId = new Map(snapshots.map(s => [s.checkId, s]));
        const rows = checks
          .filter(c => c.purpose !== 'metadata')
          .map(check => {
            const rawSnapshot = snapshotByCheckId.get(check.id) ?? null;
            const snapshot = rawSnapshot
              ? {
                  ...rawSnapshot,
                  factValue: stripInternalKeys(rawSnapshot.factValue),
                }
              : null;
            const findings =
              snapshot?.result === 'failed'
                ? findingsFromFactValue(rawSnapshot?.factValue)
                : [];
            return {
              id: check.id,
              check,
              snapshot,
              findingsCount: findings.length,
              findings,
            };
          });
        dispatch({ type: 'FETCH_SUCCESS', rows });
      })
      .catch(err => {
        if (!cancelled)
          dispatch({
            type: 'FETCH_ERROR',
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });

    return () => {
      cancelled = true;
    };
  }, [api, projectPath]);

  return state;
}
